/* eslint-disable react/display-name */
// cms mapping: https://compareasia-group.atlassian.net/wiki/spaces/CD/pages/853016721/Needs+Analysis+CMS+Data+Mapping

import React, { useState, useEffect } from 'react';
import { QuizSelectionModal, QuizCardModal, QuizResultsModal } from '@hyphengroup/cag-components';
import { getComponentConfig } from 'helpers/Utils';
import Queries from 'queries';
import {
  quizSelectionModalLoadEvent,
  quizSelectionModalCloseEvent,
  quizSelectionModalNavigationEvent,
  quizSelectionModalStartQuizEvent,
  quizSelectionModalPastResultsEvent,
  quizSelectionModalRetakeQuizEvent,
  quizQuestionModalLoadEvent,
  quizQuestionModalCloseEvent,
  quizResultsModalLoadEvent,
  quizResultsModalCloseEvent,
  quizResultsModalRecommendedProductCtaEvent,
  quizResultsModalTakeQuizAgainCtaEvent,
} from 'components/tracking/DashboardQuizTrackingUtils';
import { fetchStatus } from 'hooks/useLoginStatusCheck';
import { validatePromotionsData } from 'components/dashboard/dashboardUtils';

const QUIZ_SUBMISSION_STATUS = {
  SUCCESSFUL: 'SUCCESSFUL',
  REJECTED: 'REJECTED',
};

const withQuiz = (WrappedComponent) => defaultProps => {
  const [showQuizSelectionModal, setShowQuizSelectionModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(null);
  const [resultsModal, setResultsModal] = useState(null);
  const [listOfQuizzes, setListOfQuizzes] = useState(null);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(null);

  const props = {
    ...defaultProps,
    setShowQuizSelectionModal,
  };

  const {
    dashboardQuery, track,
    submitQuizMutation, match: { params }, localisation, defaultLanguage,
    client,
  } = defaultProps || {};
  const { componentConfig, promotions } = dashboardQuery || {};
  const quizConfig = getComponentConfig({ componentConfig }).GENERIC_QUIZ_CONFIG || {};
  const language = params?.language || defaultLanguage;
  const promotionsData = validatePromotionsData(promotions);

  useEffect(() => {
    if (showQuizSelectionModal) {
      quizSelectionModalLoadEvent(track);
    }
  }, [showQuizSelectionModal]);

  useEffect(() => {
    if (showQuizModal) {
      quizQuestionModalLoadEvent(track);
    }
  }, [showQuizModal]);

  useEffect(() => {
    if (resultsModal) {
      quizResultsModalLoadEvent(track);
    }
  }, [resultsModal]);

  useEffect(() => {
    if (promotionsData) {
      const newListOfQuizzes = promotionsData.find(promotion => promotion.quizList)
      ?.quizList
      ?.listOfQuizzes;
      newListOfQuizzes && setListOfQuizzes(newListOfQuizzes);
    }
  }, [promotionsData]);

  if (!promotionsData) return (<WrappedComponent {...props} />);

  let quizzesRecommendations = {};
  if (promotionsData) {
    quizzesRecommendations = promotionsData.find(promotion => promotion.quizList);
  }
  const availableQuiz = listOfQuizzes?.length > 0;

  function getQuizName(index) {
    return listOfQuizzes[index].header;
  }

  function handleQuizModalClose (index) {
    quizQuestionModalCloseEvent(track, getQuizName(currentQuizIndex), index);
    setCurrentQuizIndex(null);
    setShowQuizModal(false);
  }

  function handleQuizSelectionModalClose(index) {
    quizSelectionModalCloseEvent(track, getQuizName(index));
    setCurrentQuizIndex(null);
    setShowQuizSelectionModal(false);
  }

  function handleQuizResultsModalClose() {
    quizResultsModalCloseEvent(track, getQuizName(currentQuizIndex));
    setCurrentQuizIndex(null);
    setResultsModal(null);
  }

  function handleQuizSubmission(questionsState) {
    setShowQuizModal(false);
    setShowLoadingModal(true);

    const currentQuiz = listOfQuizzes[currentQuizIndex];

    client.query({
      query: Queries.GetApplicationToken,
    }).then(({ data }) => submitQuizMutation({
      variables: {
        language: localisation[language],
        applicationToken: data?.applicationToken,
        input: {
          quizTitle: currentQuiz.title,
          quizVersion: currentQuiz.version,
          answeredQuestions: questionsState.map(({ answers, title, version }) => ({
            questionVersion: version,
            questionTitle: title,
            answerId: answers,
          })),
        },
      },
    })).then(({ data }) => {
      const { result, status } = data?.quizSubmission || {};

      if (status === QUIZ_SUBMISSION_STATUS.REJECTED) {
        fetchStatus({
          onSuccess: (response) => {
            // show login form if user is logout
            if (response?.type === 'LoggedOut') {
              dashboardQuery.refetch();
              setShowLoadingModal(false);
            } else {
              // show no results modal
              setShowLoadingModal(false);
              setResultsModal({ productCount: 0 });
            }
          },
          onError: (e) => { throw new Error(e); },
        });
      } else {
        setShowLoadingModal(false);
        setResultsModal(result);

        const newListOfQuizzes = [...listOfQuizzes];
        newListOfQuizzes[currentQuizIndex].previousResult = result;
        setListOfQuizzes(newListOfQuizzes);
      }
    });
  }

  function getQuizResultsModuleProps() {
    const currentQuiz = listOfQuizzes[currentQuizIndex];
    const currentQuizConfig = currentQuiz.config || {};

    if (resultsModal && resultsModal.productCount && resultsModal.url) {
      return {
        title: currentQuizConfig.hasResultControl?.header?.replace('$numberOfResults', resultsModal.productCount),
        description: currentQuizConfig.hasResultControl?.message,
        cta: {
          text: currentQuizConfig.hasResultControl?.seeResultLabel,
          onClick: () => {
            quizResultsModalRecommendedProductCtaEvent(track, getQuizName(currentQuizIndex));
            const win = window.open(resultsModal.url, '_blank');
            win.focus();
          },
        },
      };
    }

    return {
      title: currentQuizConfig.noResultControl?.header,
      description: currentQuizConfig.noResultControl?.message,
      cta: {
        text: quizConfig.SEE_OTHER_QUIZZES_CTA_LABEL?.text,
        onClick: () => {
          setResultsModal(null);
          setShowQuizSelectionModal(true);
          setCurrentQuizIndex(null);
        },
      },
      secondaryCta: {
        text: currentQuizConfig.retakeQuizLabel,
        onClick: () => {
          quizResultsModalTakeQuizAgainCtaEvent(track, getQuizName(currentQuizIndex));
          setResultsModal(null);
          setShowQuizModal(true);
        },
      },
    };
  }

  return (
    <>
      <WrappedComponent {...props} />

      {availableQuiz
          && (showQuizSelectionModal || (!showQuizSelectionModal && showQuizModal)) && (
          <QuizSelectionModal
            onClose={handleQuizSelectionModalClose}
            isOpen={showQuizSelectionModal}
            heading={quizzesRecommendations.header}
            slashUrl={quizConfig.SLASH?.image?.url}
            activeSlideIndex={currentQuizIndex || 0}
            onNavigationButtonClick={(slideIndex) => {
              quizSelectionModalNavigationEvent(
                track,
                getQuizName(slideIndex),
                slideIndex
              );
            }}
            quizes={listOfQuizzes.map((quiz, index) => ({
              heading: quiz.header,
              subheading: quiz.previousResult
                ? quizConfig.TAKEN_QUIZ_BEFORE_LABEL?.text
                : quiz.description,
              productImage: quiz.config?.icon?.url,
              joinCTA: {
                ctaText: quiz.previousResult ? quiz.config.retakeQuizLabel : quiz.startQuizLabel,
                onClick: () => {
                  setShowQuizSelectionModal(false);
                  setShowQuizModal(true);
                  setCurrentQuizIndex(index);
                  if (quiz.previousResult) {
                    quizSelectionModalRetakeQuizEvent(track, quiz.header);
                  } else { quizSelectionModalStartQuizEvent(track, quiz.header, index); }
                },
              },
              checkResultCTA: quiz.previousResult?.url && !!quiz.previousResult?.productCount && {
                ctaText: quizConfig.SEE_PREVIOUS_RESULTS_CTA_LABEL?.text,
                onClick: () => {
                  quizSelectionModalPastResultsEvent(track, quiz.header);
                  const win = window.open(quiz.previousResult.url, '_blank');
                  win.focus();
                },
              },
            }))}
          />
      )}

      {availableQuiz && showQuizModal && currentQuizIndex !== null && (
      <QuizCardModal
        submitText={quizConfig.SEE_QUIZ_RESULTS_CTA_LABEL?.text}
        nextQuestionText={quizConfig.NEXT_QUIZ_QUESTION_CTA_LABEL?.text}
        prevQuestionText={quizConfig.PREVIOUS_QUIZ_QUESTION_CTA_LABEL?.text}
        onClose={handleQuizModalClose}
        isOpen={showQuizModal}
        onSubmit={handleQuizSubmission}
        onGoBack={() => {
          setShowQuizModal(false);
          setShowQuizSelectionModal(true);
        }}
        questionTemplate={quizConfig.QUIZ_QUESTION_NUMBER_TEMPLATE?.text}
        lastSlideHeading={quizConfig.LAST_QUIZ_QUESTION_BANNER_TEXT?.text}
        questions={listOfQuizzes[currentQuizIndex]
          .listOfQuestions
          .map(({
            answerSelectionType, question, answer, version,
            title,
          }) => ({
            version,
            type: answerSelectionType,
            title,
            question,
            answers: [],
            choices: answer.map(({ index, display }) => ({
              id: index,
              text: display,
            })),
          }))}
      />
      )}

      {showLoadingModal && (
      <QuizResultsModal
        isOpen={true}
        onClose={() => setShowLoadingModal(false)}
        title={quizConfig.LOADING_MATCHES_LABEL?.text}
        loading
      />
      )}

      {resultsModal && currentQuizIndex !== null && (
      <QuizResultsModal
        isOpen={true}
        onClose={handleQuizResultsModalClose}
        image={{
          src: listOfQuizzes[currentQuizIndex].config?.icon?.url,
          alt: listOfQuizzes[currentQuizIndex].config?.icon?.filename,
        }}
        {...getQuizResultsModuleProps()}
      />
      )}
    </>
  );
};

export default withQuiz;
